import { cn } from '@/lib/utils';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@radix-ui/react-popover';
import { Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface EmojiPickerProps {
	value: string;
	onChange: (emoji: string) => void;
	className?: string;
	imageChoices?: EmojiData[];
}

export interface EmojiData {
	icon: string;
	searchValues: string[];
}

export const EmojiPicker: React.FC<EmojiPickerProps> = React.memo(
	({ value, onChange, className, imageChoices }) => {
		const [searchQuery, setSearchQuery] = useState('');
		const [isOpen, setIsOpen] = useState(false);

		const filteredEmojis = useMemo(() => {
			const baseEmojis = EMOJI_LIST.filter((emoji) =>
				emoji.searchValues.some((term) =>
					term.toLowerCase().includes(searchQuery.toLowerCase())
				)
			);

			if (!imageChoices?.length) return baseEmojis;

			return [...imageChoices, ...baseEmojis];
		}, [searchQuery, imageChoices]);

		// Set default value to first emoji in filteredEmojis if imageChoices exist and no value is set
		useEffect(() => {
			if (imageChoices?.length && !value && filteredEmojis.length > 0) {
				onChange(filteredEmojis[0].icon);
			}
		}, [imageChoices, value, filteredEmojis, onChange]);

		const handleEmojiClick = useCallback(
			(emoji: string) => {
				onChange(emoji);
				setIsOpen(false);
			},
			[onChange]
		);

		const EmojiButton = useCallback(
			({ emoji }: { emoji: EmojiData }) => {
				const isImage =
					emoji.icon.startsWith('http://') || emoji.icon.startsWith('https://');

				return (
					<button
						className={cn(
							'w-8 h-8 flex items-center justify-center text-xl rounded-md hover:bg-accent transition-colors',
							value === emoji.icon && 'bg-accent'
						)}
						onClick={() => handleEmojiClick(emoji.icon)}
						aria-label={`Select ${isImage ? 'custom' : emoji.icon} emoji`}>
						{isImage ? (
							<img
								src={emoji.icon}
								alt='Custom icon'
								className='w-5 h-5 object-contain'
							/>
						) : (
							emoji.icon
						)}
					</button>
				);
			},
			[value, handleEmojiClick]
		);

		return (
			<Popover open={isOpen} onOpenChange={setIsOpen}>
				<PopoverTrigger asChild>
					<Button
						variant='outline'
						className={cn(
							'w-12 h-12 p-0 text-2xl flex items-center justify-center',
							className
						)}
						style={{
							backgroundColor: 'var(--input-background-color)',
							borderColor: 'var(--input-background-color)',
						}}
						aria-label='Select emoji'>
						{value ? (
							value.startsWith('http://') || value.startsWith('https://') ? (
								<img
									src={value}
									alt='Selected icon'
									className='w-6 h-6 object-contain'
								/>
							) : (
								value
							)
						) : (
							'😊'
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className='bg-white w-[320px] p-0 mt-2 z-[100] rounded-lg'
					style={{
						backgroundColor: 'var(--input-background-color)',
					}}
					align='start'
					side='bottom'
					sideOffset={4}>
					<div className='p-2'>
						<div className='relative flex flex-row items-center'>
							<Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
							<Input
								placeholder='Search emojis...'
								className='pl-8 h-8'
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
						<div className='mt-2 max-h-[200px] overflow-y-auto'>
							<div className='grid grid-cols-8 gap-1 p-1'>
								{filteredEmojis.map((emoji) => (
									<EmojiButton key={emoji.icon} emoji={emoji} />
								))}
							</div>
						</div>
					</div>
				</PopoverContent>
			</Popover>
		);
	}
);

EmojiPicker.displayName = 'EmojiPicker';

export const EMOJI_LIST: EmojiData[] = [
	{ icon: '😀', searchValues: ['grinning', 'face', 'happy', 'smile'] },
	{ icon: '😃', searchValues: ['smile', 'happy', 'joy', 'grin'] },
	{ icon: '😄', searchValues: ['smile', 'happy', 'joy', 'laugh'] },
	{ icon: '😁', searchValues: ['grin', 'happy', 'joy', 'smile'] },
	{ icon: '😅', searchValues: ['sweat', 'smile', 'happy', 'relief'] },
	{ icon: '😆', searchValues: ['laugh', 'happy', 'joy', 'lol'] },
	{ icon: '🤣', searchValues: ['rofl', 'laugh', 'rolling', 'floor'] },
	{ icon: '😂', searchValues: ['joy', 'tears', 'laugh', 'happy'] },
	{ icon: '🙂', searchValues: ['slight', 'smile', 'happy', 'face'] },
	{ icon: '🙃', searchValues: ['upside', 'down', 'face', 'silly'] },
	{ icon: '😉', searchValues: ['wink', 'face', 'silly', 'playful'] },
	{ icon: '😊', searchValues: ['blush', 'happy', 'smile', 'pleased'] },
	{ icon: '😇', searchValues: ['angel', 'halo', 'innocent', 'heaven'] },
	{ icon: '🥰', searchValues: ['love', 'heart', 'face', 'adore'] },
	{ icon: '😍', searchValues: ['heart', 'eyes', 'love', 'adore'] },
	{ icon: '🤩', searchValues: ['star', 'eyes', 'excited', 'amazed'] },
	{ icon: '😘', searchValues: ['kiss', 'face', 'love', 'heart'] },
	{ icon: '😗', searchValues: ['kissing', 'face', 'love', 'affection'] },
	{ icon: '😚', searchValues: ['kiss', 'face', 'love', 'affection'] },
	{ icon: '😙', searchValues: ['kiss', 'face', 'love', 'affection'] },
	{ icon: '😋', searchValues: ['yum', 'face', 'tasty', 'delicious'] },
	{ icon: '😛', searchValues: ['tongue', 'face', 'silly', 'playful'] },
	{ icon: '😜', searchValues: ['wink', 'tongue', 'silly', 'playful'] },
	{ icon: '🤪', searchValues: ['crazy', 'face', 'silly', 'zany'] },
	{ icon: '😝', searchValues: ['tongue', 'face', 'silly', 'playful'] },
	{ icon: '🤑', searchValues: ['money', 'face', 'rich', 'wealth'] },
	{ icon: '🤗', searchValues: ['hug', 'face', 'love', 'affection'] },
	{ icon: '🤭', searchValues: ['hand', 'mouth', 'quiet', 'secret'] },
	{ icon: '🤫', searchValues: ['shush', 'quiet', 'silence', 'secret'] },
	{ icon: '🤔', searchValues: ['thinking', 'face', 'ponder', 'consider'] },
	{ icon: '🤐', searchValues: ['zipper', 'mouth', 'silence', 'quiet'] },
	{ icon: '🤨', searchValues: ['raised', 'eyebrow', 'skeptical', 'doubt'] },
	{ icon: '😐', searchValues: ['neutral', 'face', 'expressionless', 'blank'] },
	{
		icon: '😑',
		searchValues: ['expressionless', 'face', 'unimpressed', 'meh'],
	},
	{ icon: '😶', searchValues: ['face', 'no', 'mouth', 'silent'] },
	{ icon: '😏', searchValues: ['smirk', 'face', 'smug', 'sly'] },
	{ icon: '😒', searchValues: ['unamused', 'face', 'bored', 'unhappy'] },
	{ icon: '🙄', searchValues: ['eye', 'roll', 'annoyed', 'exasperated'] },
	{ icon: '😬', searchValues: 'grimace face pain awkward'.split(' ') },
	{ icon: '🤥', searchValues: 'lying face lie liar dishonest'.split(' ') },
	{ icon: '😌', searchValues: 'relieved face calm peaceful'.split(' ') },
	{ icon: '😔', searchValues: 'pensive face sad thoughtful'.split(' ') },
	{ icon: '😪', searchValues: 'sleepy face tired drowsy'.split(' ') },
	{ icon: '🤤', searchValues: 'drooling face drool hungry'.split(' ') },
	{ icon: '😴', searchValues: 'sleeping face sleep tired'.split(' ') },
	{ icon: '😷', searchValues: 'sick face ill mask'.split(' ') },
	{ icon: '🤒', searchValues: 'fever face sick ill'.split(' ') },
	{ icon: '🤕', searchValues: 'injured face hurt bandage'.split(' ') },
	{ icon: '🤢', searchValues: 'nauseated face sick disgust'.split(' ') },
	{ icon: '🤮', searchValues: 'vomiting face sick puke'.split(' ') },
	{ icon: '🤧', searchValues: 'sneezing face sick achoo'.split(' ') },
	{ icon: '🥵', searchValues: 'hot face sweat heat'.split(' ') },
	{ icon: '🥶', searchValues: 'cold face freeze frozen'.split(' ') },
	{ icon: '🥴', searchValues: 'woozy face dizzy drunk'.split(' ') },
	{ icon: '😵', searchValues: 'dizzy face confused'.split(' ') },
	{ icon: '🤯', searchValues: 'exploding head mind blown'.split(' ') },
	{ icon: '🤠', searchValues: 'cowboy hat face western'.split(' ') },
	{ icon: '🥳', searchValues: 'party face celebrate celebration'.split(' ') },
	{ icon: '😎', searchValues: 'cool face sunglasses'.split(' ') },
	{ icon: '🤓', searchValues: 'nerd face glasses smart'.split(' ') },
	{ icon: '🧐', searchValues: 'monocle face detective'.split(' ') },
	{ icon: '😕', searchValues: 'confused face puzzled'.split(' ') },
	{ icon: '😟', searchValues: 'worried face concern'.split(' ') },
	{ icon: '🙁', searchValues: 'slightly frowning face sad'.split(' ') },
	{ icon: '☹️', searchValues: 'frowning face sad unhappy'.split(' ') },
	{ icon: '😮', searchValues: 'surprised face wow oh'.split(' ') },
	{ icon: '😯', searchValues: 'hushed face silence quiet'.split(' ') },
	{ icon: '😲', searchValues: 'astonished face shocked'.split(' ') },
	{ icon: '😳', searchValues: 'flushed face embarrassed'.split(' ') },
	{ icon: '🥺', searchValues: 'pleading face puppy eyes'.split(' ') },
	{ icon: '😦', searchValues: 'frowning face open mouth sad'.split(' ') },
	{ icon: '😧', searchValues: 'anguished face pain'.split(' ') },
	{ icon: '😨', searchValues: 'fearful face scared'.split(' ') },
	{ icon: '😰', searchValues: 'anxious face sweat nervous'.split(' ') },
	{ icon: '😥', searchValues: 'sad relieved face tear'.split(' ') },
	{ icon: '😢', searchValues: 'crying face sad tear'.split(' ') },
	{ icon: '😭', searchValues: 'loudly crying face sob'.split(' ') },
	{ icon: '😱', searchValues: 'screaming face fear'.split(' ') },
	{ icon: '😖', searchValues: 'confounded face confused'.split(' ') },
	{ icon: '😣', searchValues: 'persevering face struggle'.split(' ') },
	{ icon: '😞', searchValues: 'disappointed face sad'.split(' ') },
	{ icon: '😓', searchValues: 'downcast face sweat sad'.split(' ') },
	{ icon: '😩', searchValues: 'weary face tired'.split(' ') },
	{ icon: '😫', searchValues: 'tired face exhausted'.split(' ') },
	{ icon: '🥱', searchValues: 'yawning face tired sleepy'.split(' ') },
	{ icon: '😤', searchValues: 'steam from nose face angry'.split(' ') },
	{ icon: '😡', searchValues: 'pouting face angry mad'.split(' ') },
	{ icon: '😠', searchValues: 'angry face mad upset'.split(' ') },
	{ icon: '🤬', searchValues: 'cursing face swear angry'.split(' ') },
	{ icon: '😈', searchValues: 'smiling face horns devil'.split(' ') },
	{ icon: '👿', searchValues: 'angry face horns devil'.split(' ') },
	{ icon: '💀', searchValues: 'skull dead skeleton'.split(' ') },
	{ icon: '☠️', searchValues: 'skull crossbones pirate danger'.split(' ') },
	{ icon: '💩', searchValues: 'pile of poo poop shit'.split(' ') },
	{ icon: '🤡', searchValues: 'clown face circus'.split(' ') },
	{ icon: '👹', searchValues: 'ogre monster japanese'.split(' ') },
	{ icon: '👺', searchValues: 'goblin monster japanese'.split(' ') },
	{ icon: '👻', searchValues: 'ghost halloween spooky'.split(' ') },
	{ icon: '👽', searchValues: 'alien space extraterrestrial'.split(' ') },
	{ icon: '👾', searchValues: 'alien monster space'.split(' ') },
	{ icon: '🤖', searchValues: 'robot bot android'.split(' ') },
	{ icon: '😺', searchValues: 'grinning cat face happy'.split(' ') },
	{ icon: '😸', searchValues: 'grinning cat face smile eyes'.split(' ') },
	{ icon: '😹', searchValues: 'cat face tears joy'.split(' ') },
	{ icon: '😻', searchValues: 'heart eyes cat face love'.split(' ') },
	{ icon: '😼', searchValues: 'smirk cat face smug'.split(' ') },
	{ icon: '😽', searchValues: 'kissing cat face love'.split(' ') },
	{ icon: '🙀', searchValues: 'weary cat face tired'.split(' ') },
	{ icon: '😿', searchValues: 'crying cat face sad'.split(' ') },
	{ icon: '😾', searchValues: 'pouting cat face angry'.split(' ') },

	// Animals
	{ icon: '🐶', searchValues: ['dog', 'puppy', 'animal', 'pet', 'cute'] },
	{ icon: '🐱', searchValues: ['cat', 'kitten', 'animal', 'pet', 'cute'] },
	{ icon: '🐭', searchValues: ['mouse', 'rodent', 'animal', 'pet'] },
	{ icon: '🐹', searchValues: ['hamster', 'rodent', 'animal', 'pet', 'cute'] },
	{ icon: '🐰', searchValues: ['rabbit', 'bunny', 'animal', 'pet', 'cute'] },
	{ icon: '🦊', searchValues: ['fox', 'animal', 'wild', 'nature'] },
	{ icon: '🐻', searchValues: ['bear', 'animal', 'wild', 'nature'] },
	{ icon: '🐼', searchValues: ['panda', 'bear', 'animal', 'cute'] },
	{ icon: '🐨', searchValues: ['koala', 'animal', 'australia', 'cute'] },
	{ icon: '🐯', searchValues: ['tiger', 'animal', 'wild', 'cat', 'nature'] },
	{ icon: '🦁', searchValues: ['lion', 'animal', 'wild', 'cat', 'nature'] },
	{ icon: '🐮', searchValues: ['cow', 'animal', 'farm', 'nature'] },
	{ icon: '🐷', searchValues: ['pig', 'animal', 'farm', 'nature'] },
	{ icon: '🐸', searchValues: ['frog', 'animal', 'amphibian', 'nature'] },
	{ icon: '🐵', searchValues: ['monkey', 'animal', 'wild', 'nature'] },
	{ icon: '🦒', searchValues: ['giraffe', 'animal', 'wild', 'tall', 'nature'] },
	{ icon: '🦘', searchValues: ['kangaroo', 'animal', 'australia', 'jump'] },
	{ icon: '🦡', searchValues: ['badger', 'animal', 'wild', 'nature'] },

	// Birds
	{ icon: '🦅', searchValues: ['eagle', 'bird', 'animal', 'wild', 'nature'] },
	{ icon: '🦆', searchValues: ['duck', 'bird', 'animal', 'water', 'nature'] },
	{ icon: '🦉', searchValues: ['owl', 'bird', 'animal', 'night', 'wise'] },
	{
		icon: '🦜',
		searchValues: ['parrot', 'bird', 'animal', 'tropical', 'colorful'],
	},
	{ icon: '🐧', searchValues: ['penguin', 'bird', 'animal', 'cold', 'cute'] },
	{ icon: '🦢', searchValues: ['swan', 'bird', 'animal', 'elegant', 'nature'] },

	// Sea Life
	{ icon: '🐋', searchValues: ['whale', 'animal', 'ocean', 'sea', 'marine'] },
	{ icon: '🐬', searchValues: ['dolphin', 'animal', 'ocean', 'sea', 'marine'] },
	{ icon: '🦈', searchValues: ['shark', 'animal', 'ocean', 'sea', 'marine'] },
	{ icon: '🐠', searchValues: ['fish', 'tropical', 'ocean', 'sea', 'marine'] },
	{ icon: '🐡', searchValues: ['blowfish', 'fish', 'ocean', 'sea', 'marine'] },
	{ icon: '🦑', searchValues: ['squid', 'animal', 'ocean', 'sea', 'marine'] },
	{ icon: '🐙', searchValues: ['octopus', 'animal', 'ocean', 'sea', 'marine'] },

	// Insects
	{ icon: '🦋', searchValues: ['butterfly', 'insect', 'nature', 'beautiful'] },
	{ icon: '🐝', searchValues: ['bee', 'insect', 'honey', 'nature', 'buzz'] },
	{ icon: '🐞', searchValues: ['ladybug', 'insect', 'nature', 'cute'] },
	{ icon: '🦗', searchValues: ['cricket', 'insect', 'nature', 'grasshopper'] },
	{ icon: '🕷️', searchValues: ['spider', 'insect', 'web', 'nature'] },

	// Nature
	{
		icon: '🌸',
		searchValues: ['cherry', 'blossom', 'flower', 'nature', 'spring'],
	},
	{ icon: '🌺', searchValues: ['hibiscus', 'flower', 'tropical', 'nature'] },
	{
		icon: '🌹',
		searchValues: ['rose', 'flower', 'love', 'nature', 'romantic'],
	},
	{ icon: '🌷', searchValues: ['tulip', 'flower', 'nature', 'spring'] },
	{ icon: '🌻', searchValues: ['sunflower', 'flower', 'nature', 'summer'] },
	{ icon: '🌳', searchValues: ['tree', 'nature', 'plant', 'forest'] },
	{ icon: '🌴', searchValues: ['palm', 'tree', 'tropical', 'beach', 'nature'] },
	{ icon: '🌵', searchValues: ['cactus', 'plant', 'desert', 'nature'] },
	{ icon: '🍄', searchValues: ['mushroom', 'fungus', 'nature', 'toadstool'] },
	{ icon: '🌿', searchValues: ['herb', 'plant', 'leaf', 'nature', 'green'] },
	{ icon: '🍀', searchValues: ['four', 'leaf', 'clover', 'lucky', 'nature'] },
	{ icon: '🍁', searchValues: ['maple', 'leaf', 'fall', 'autumn', 'nature'] },
	{ icon: '🌊', searchValues: ['wave', 'ocean', 'sea', 'water', 'nature'] },

	// Weather & Sky
	{ icon: '🌞', searchValues: ['sun', 'face', 'weather', 'sunny', 'nature'] },
	{ icon: '🌝', searchValues: ['full', 'moon', 'face', 'night', 'nature'] },
	{ icon: '⭐', searchValues: ['star', 'sky', 'night', 'space', 'nature'] },
	{ icon: '🌈', searchValues: ['rainbow', 'weather', 'colorful', 'nature'] },
	{ icon: '☁️', searchValues: ['cloud', 'weather', 'sky', 'nature'] },
	{ icon: '⚡', searchValues: ['lightning', 'thunder', 'weather', 'nature'] },
	{ icon: '❄️', searchValues: ['snowflake', 'winter', 'cold', 'nature'] },
	{ icon: '🌪️', searchValues: ['tornado', 'weather', 'storm', 'nature'] },

	// Food
	{ icon: '🍎', searchValues: ['apple', 'fruit', 'red', 'food', 'healthy'] },
	{ icon: '🍐', searchValues: ['pear', 'fruit', 'food', 'green', 'healthy'] },
	{
		icon: '🍊',
		searchValues: ['orange', 'fruit', 'citrus', 'food', 'healthy'],
	},
	{ icon: '🍋', searchValues: ['lemon', 'fruit', 'citrus', 'yellow', 'food'] },
	{
		icon: '🍌',
		searchValues: ['banana', 'fruit', 'food', 'yellow', 'healthy'],
	},
	{ icon: '🍉', searchValues: ['watermelon', 'fruit', 'food', 'summer'] },
	{ icon: '🍇', searchValues: ['grapes', 'fruit', 'food', 'wine'] },
	{ icon: '🍓', searchValues: ['strawberry', 'fruit', 'food', 'red', 'berry'] },
	{
		icon: '🫐',
		searchValues: ['blueberries', 'fruit', 'food', 'berry', 'healthy'],
	},
	{ icon: '🥝', searchValues: ['kiwi', 'fruit', 'food', 'green', 'healthy'] },
	{ icon: '🍅', searchValues: ['tomato', 'vegetable', 'fruit', 'food', 'red'] },
	{
		icon: '🥑',
		searchValues: ['avocado', 'fruit', 'food', 'healthy', 'guacamole'],
	},

	// Vegetables
	{
		icon: '🥕',
		searchValues: ['carrot', 'vegetable', 'food', 'orange', 'healthy'],
	},
	{ icon: '🌽', searchValues: ['corn', 'vegetable', 'food', 'maize'] },
	{
		icon: '🥦',
		searchValues: ['broccoli', 'vegetable', 'food', 'green', 'healthy'],
	},
	{
		icon: '🥬',
		searchValues: ['leafy green', 'lettuce', 'vegetable', 'food', 'healthy'],
	},
	{
		icon: '🥒',
		searchValues: ['cucumber', 'vegetable', 'food', 'pickle', 'green'],
	},
	{ icon: '🫑', searchValues: ['bell pepper', 'vegetable', 'food', 'healthy'] },
	{ icon: '🧄', searchValues: ['garlic', 'vegetable', 'food', 'seasoning'] },
	{ icon: '🧅', searchValues: ['onion', 'vegetable', 'food', 'seasoning'] },

	// Prepared Food
	{ icon: '🍕', searchValues: ['pizza', 'food', 'italian', 'cheese', 'slice'] },
	{ icon: '🍔', searchValues: ['hamburger', 'buacrger', 'food', 'fast food'] },
	{
		icon: '🍟',
		searchValues: ['french fries', 'fries', 'food', 'potato', 'fast food'],
	},
	{ icon: '🌭', searchValues: ['hot dog', 'food', 'sausage', 'fast food'] },
	{ icon: '🥪', searchValues: ['sandwich', 'food', 'lunch', 'bread'] },
	{ icon: '🌮', searchValues: ['taco', 'food', 'mexican', 'tortilla'] },
	{ icon: '🌯', searchValues: ['burrito', 'food', 'mexican', 'wrap'] },
	{
		icon: '🥗',
		searchValues: ['salad', 'food', 'healthy', 'green', 'vegetables'],
	},
	{
		icon: '🍝',
		searchValues: ['spaghetti', 'pasta', 'food', 'italian', 'noodles'],
	},
	{
		icon: '🍜',
		searchValues: ['ramen', 'noodles', 'food', 'japanese', 'soup'],
	},
	{ icon: '🍲', searchValues: ['pot of food', 'stew', 'soup', 'food', 'hot'] },
	{ icon: '🍛', searchValues: ['curry', 'food', 'indian', 'spicy', 'rice'] },
	{ icon: '🍣', searchValues: ['sushi', 'food', 'japanese', 'fish', 'rice'] },
	{ icon: '🥟', searchValues: ['dumpling', 'food', 'asian', 'potsticker'] },

	// Sweets & Desserts
	{
		icon: '🍦',
		searchValues: ['ice cream', 'soft serve', 'dessert', 'sweet', 'cold'],
	},
	{ icon: '🍨', searchValues: ['ice cream', 'dessert', 'sweet', 'cold'] },
	{
		icon: '🍰',
		searchValues: ['cake', 'dessert', 'sweet', 'birthday', 'slice'],
	},
	{
		icon: '🧁',
		searchValues: ['cupcake', 'dessert', 'sweet', 'cake', 'frosting'],
	},
	{ icon: '🥧', searchValues: ['pie', 'dessert', 'sweet', 'pastry'] },
	{ icon: '🍪', searchValues: ['cookie', 'dessert', 'sweet', 'biscuit'] },
	{ icon: '🍫', searchValues: ['chocolate', 'candy', 'dessert', 'sweet'] },
	{ icon: '🍬', searchValues: ['candy', 'sweet', 'dessert', 'sugar'] },

	// Drinks
	{
		icon: '☕',
		searchValues: ['coffee', 'drink', 'hot', 'caffeine', 'beverage'],
	},
	{ icon: '🫖', searchValues: ['teapot', 'tea', 'drink', 'hot', 'beverage'] },
	{
		icon: '🍵',
		searchValues: ['tea', 'green tea', 'drink', 'hot', 'beverage'],
	},
	{
		icon: '🥤',
		searchValues: ['soda', 'soft drink', 'beverage', 'cold', 'cup'],
	},
	{ icon: '🧃', searchValues: ['juice box', 'drink', 'beverage', 'fruit'] },
	{ icon: '🥛', searchValues: ['milk', 'drink', 'dairy', 'beverage', 'glass'] },
	{ icon: '🍷', searchValues: ['wine', 'alcohol', 'drink', 'beverage', 'red'] },
	{
		icon: '🍸',
		searchValues: ['martini', 'cocktail', 'alcohol', 'drink', 'glass'],
	},
	{
		icon: '🍺',
		searchValues: ['beer', 'alcohol', 'drink', 'beverage', 'brew'],
	},
	{
		icon: '🥂',
		searchValues: ['champagne', 'sparkling', 'celebration', 'alcohol', 'drink'],
	},
	{ icon: '🧊', searchValues: ['ice cube', 'cold', 'frozen', 'drink'] },

	// Sports
	{ icon: '⚽', searchValues: ['soccer', 'football', 'sport', 'ball', 'game'] },
	{ icon: '🏀', searchValues: ['basketball', 'sport', 'ball', 'game', 'hoop'] },
	{
		icon: '🏈',
		searchValues: ['football', 'american football', 'sport', 'ball', 'game'],
	},
	{ icon: '⚾', searchValues: ['baseball', 'sport', 'ball', 'game', 'bat'] },
	{ icon: '🎾', searchValues: ['tennis', 'sport', 'ball', 'game', 'racket'] },
	{ icon: '🏐', searchValues: ['volleyball', 'sport', 'ball', 'game', 'net'] },
	{ icon: '🏉', searchValues: ['rugby', 'sport', 'ball', 'game', 'football'] },
	{ icon: '🎯', searchValues: ['dart', 'target', 'game', 'sport', 'aim'] },
	{ icon: '🏹', searchValues: ['archery', 'bow', 'arrow', 'sport', 'target'] },
	{ icon: '🎳', searchValues: ['bowling', 'game', 'sport', 'ball', 'pin'] },
	{
		icon: '🏓',
		searchValues: ['ping pong', 'table tennis', 'sport', 'game', 'paddle'],
	},
	{
		icon: '🏸',
		searchValues: ['badminton', 'sport', 'game', 'racket', 'shuttlecock'],
	},
	{
		icon: '🏒',
		searchValues: ['hockey', 'ice hockey', 'sport', 'game', 'stick'],
	},
	{ icon: '⛳', searchValues: ['golf', 'sport', 'game', 'hole', 'green'] },
	{
		icon: '⛸️',
		searchValues: ['ice skate', 'skating', 'sport', 'winter', 'ice'],
	},
	{ icon: '🎿', searchValues: ['ski', 'sport', 'winter', 'snow', 'mountain'] },
	{
		icon: '🏂',
		searchValues: ['snowboard', 'sport', 'winter', 'snow', 'mountain'],
	},
	{ icon: '🏊', searchValues: ['swimming', 'sport', 'water', 'pool', 'swim'] },
	{ icon: '🏄', searchValues: ['surfing', 'sport', 'water', 'wave', 'ocean'] },
	{ icon: '🚣', searchValues: ['rowing', 'boat', 'sport', 'water', 'paddle'] },
	{
		icon: '🚴',
		searchValues: ['cycling', 'bike', 'sport', 'exercise', 'ride'],
	},
	{
		icon: '🚵',
		searchValues: ['mountain biking', 'bike', 'sport', 'mountain', 'ride'],
	},
	{
		icon: '🤸',
		searchValues: ['gymnastics', 'sport', 'exercise', 'flexible', 'acrobatics'],
	},
	{
		icon: '🤼',
		searchValues: ['wrestling', 'sport', 'fight', 'competition', 'grapple'],
	},
	{
		icon: '🤽',
		searchValues: ['water polo', 'sport', 'water', 'pool', 'ball'],
	},
	{ icon: '🤾', searchValues: ['handball', 'sport', 'ball', 'game', 'team'] },
	{
		icon: '🏋️',
		searchValues: ['weight lifting', 'gym', 'exercise', 'strength', 'fitness'],
	},

	// Music & Arts
	{
		icon: '🎤',
		searchValues: ['microphone', 'singing', 'music', 'karaoke', 'voice'],
	},
	{
		icon: '🎧',
		searchValues: ['headphones', 'music', 'listen', 'audio', 'sound'],
	},
	{
		icon: '🎼',
		searchValues: ['sheet music', 'music', 'notes', 'score', 'composition'],
	},
	{
		icon: '🎹',
		searchValues: ['piano', 'keyboard', 'music', 'instrument', 'keys'],
	},
	{
		icon: '🎸',
		searchValues: ['guitar', 'music', 'instrument', 'rock', 'strings'],
	},
	{
		icon: '🎷',
		searchValues: ['saxophone', 'music', 'instrument', 'jazz', 'brass'],
	},
	{
		icon: '🎺',
		searchValues: ['trumpet', 'music', 'instrument', 'brass', 'horn'],
	},
	{
		icon: '🎻',
		searchValues: ['violin', 'music', 'instrument', 'strings', 'classical'],
	},
	{
		icon: '🥁',
		searchValues: ['drum', 'music', 'instrument', 'percussion', 'beat'],
	},
	{
		icon: '🎨',
		searchValues: ['art', 'painting', 'drawing', 'creative', 'artist'],
	},
	{
		icon: '🎭',
		searchValues: ['theater', 'drama', 'acting', 'stage', 'performance'],
	},
	{
		icon: '🎬',
		searchValues: ['movie', 'film', 'cinema', 'camera', 'clapper'],
	},
	{
		icon: '🎮',
		searchValues: ['video game', 'gaming', 'controller', 'play', 'console'],
	},
	{
		icon: '🎲',
		searchValues: ['dice', 'game', 'gambling', 'random', 'chance'],
	},
	{
		icon: '🎪',
		searchValues: ['circus', 'tent', 'entertainment', 'show', 'carnival'],
	},

	// Travel & Outdoor
	{
		icon: '✈️',
		searchValues: ['airplane', 'flight', 'travel', 'airport', 'fly'],
	},
	{
		icon: '🚀',
		searchValues: ['rocket', 'space', 'launch', 'ship', 'science'],
	},
	{
		icon: '🚁',
		searchValues: ['helicopter', 'aircraft', 'fly', 'transport', 'rescue'],
	},
	{ icon: '🚢', searchValues: ['ship', 'boat', 'cruise', 'sea', 'travel'] },
	{
		icon: '🚂',
		searchValues: ['train', 'locomotive', 'railway', 'transport', 'travel'],
	},
	{
		icon: '🚗',
		searchValues: ['car', 'automobile', 'vehicle', 'drive', 'transport'],
	},
	{
		icon: '🚲',
		searchValues: ['bicycle', 'bike', 'cycle', 'transport', 'ride'],
	},
	{
		icon: '🏕️',
		searchValues: ['camping', 'tent', 'outdoor', 'nature', 'wilderness'],
	},
	{
		icon: '🏖️',
		searchValues: ['beach', 'sand', 'ocean', 'vacation', 'summer'],
	},
	{
		icon: '⛰️',
		searchValues: ['mountain', 'peak', 'nature', 'hiking', 'climb'],
	},
	{
		icon: '🌋',
		searchValues: ['volcano', 'mountain', 'eruption', 'lava', 'nature'],
	},
	{ icon: '🏜️', searchValues: ['desert', 'sand', 'dry', 'hot', 'nature'] },
	{
		icon: '🏝️',
		searchValues: ['island', 'tropical', 'beach', 'ocean', 'paradise'],
	},
	{
		icon: '🏞️',
		searchValues: ['national park', 'nature', 'scenic', 'landscape', 'view'],
	},
	{ icon: '🌅', searchValues: ['sunrise', 'morning', 'dawn', 'sun', 'nature'] },
	{
		icon: '🌄',
		searchValues: [
			'sunrise over mountains',
			'morning',
			'dawn',
			'nature',
			'view',
		],
	},
	{
		icon: '🌠',
		searchValues: ['shooting star', 'night', 'sky', 'wish', 'meteor'],
	},
	{
		icon: '🎆',
		searchValues: [
			'fireworks',
			'celebration',
			'festival',
			'explosion',
			'night',
		],
	},
	{
		icon: '🎇',
		searchValues: ['sparkler', 'firework', 'celebration', 'light', 'night'],
	},

	// Office & School
	{
		icon: '📚',
		searchValues: ['books', 'library', 'reading', 'education', 'school'],
	},
	{
		icon: '📖',
		searchValues: ['book', 'reading', 'open book', 'education', 'literature'],
	},
	{
		icon: '📝',
		searchValues: ['memo', 'note', 'writing', 'document', 'paper'],
	},
	{
		icon: '✏️',
		searchValues: ['pencil', 'writing', 'drawing', 'stationery', 'school'],
	},
	{
		icon: '✒️',
		searchValues: ['fountain pen', 'writing', 'ink', 'calligraphy', 'elegant'],
	},
	{
		icon: '📏',
		searchValues: ['ruler', 'measure', 'straight', 'school', 'stationery'],
	},
	{
		icon: '📐',
		searchValues: ['triangle ruler', 'measure', 'angle', 'school', 'geometry'],
	},
	{
		icon: '📎',
		searchValues: ['paperclip', 'office', 'document', 'attach', 'stationery'],
	},
	{
		icon: '📌',
		searchValues: ['pushpin', 'pin', 'attach', 'board', 'stationery'],
	},
	{
		icon: '✂️',
		searchValues: ['scissors', 'cut', 'craft', 'stationery', 'tool'],
	},
	{
		icon: '📁',
		searchValues: ['file folder', 'organize', 'document', 'office', 'folder'],
	},
	{
		icon: '📂',
		searchValues: [
			'open file folder',
			'organize',
			'document',
			'office',
			'folder',
		],
	},
	{
		icon: '📅',
		searchValues: ['calendar', 'date', 'schedule', 'plan', 'time'],
	},
	{
		icon: '📆',
		searchValues: ['tear-off calendar', 'date', 'schedule', 'plan', 'time'],
	},
	{
		icon: '📊',
		searchValues: ['bar chart', 'graph', 'statistics', 'data', 'analytics'],
	},
	{
		icon: '📈',
		searchValues: ['increasing chart', 'growth', 'trend', 'statistics', 'data'],
	},
	{
		icon: '📉',
		searchValues: [
			'decreasing chart',
			'decline',
			'trend',
			'statistics',
			'data',
		],
	},
	{
		icon: '📋',
		searchValues: ['clipboard', 'document', 'copy', 'office', 'list'],
	},
	{
		icon: '📑',
		searchValues: ['bookmark tabs', 'organize', 'mark', 'document', 'tabs'],
	},
	{
		icon: '📒',
		searchValues: ['ledger', 'notebook', 'accounting', 'finance', 'book'],
	},
	{
		icon: '📓',
		searchValues: ['notebook', 'writing', 'notes', 'journal', 'book'],
	},
	{
		icon: '📔',
		searchValues: ['decorated notebook', 'writing', 'notes', 'journal', 'book'],
	},
	{
		icon: '📕',
		searchValues: ['closed book', 'reading', 'literature', 'education', 'book'],
	},
	{
		icon: '📗',
		searchValues: ['green book', 'reading', 'literature', 'education', 'book'],
	},
	{
		icon: '📘',
		searchValues: ['blue book', 'reading', 'literature', 'education', 'book'],
	},
	{
		icon: '📙',
		searchValues: ['orange book', 'reading', 'literature', 'education', 'book'],
	},
	{
		icon: '📫',
		searchValues: ['closed mailbox', 'mail', 'post', 'letter', 'communication'],
	},
	{
		icon: '📪',
		searchValues: ['open mailbox', 'mail', 'post', 'letter', 'communication'],
	},
	{
		icon: '📬',
		searchValues: [
			'mailbox with mail',
			'mail',
			'post',
			'letter',
			'communication',
		],
	},
	{
		icon: '📭',
		searchValues: ['mailbox with no mail', 'mail', 'post', 'letter', 'empty'],
	},
	{
		icon: '📮',
		searchValues: ['postbox', 'mail', 'post', 'letter', 'communication'],
	},
	{
		icon: '📦',
		searchValues: ['package', 'box', 'delivery', 'shipping', 'mail'],
	},
	{
		icon: '📯',
		searchValues: ['postal horn', 'mail', 'post', 'announcement', 'horn'],
	},
	{
		icon: '📠',
		searchValues: ['fax machine', 'fax', 'document', 'office', 'communication'],
	},
	{
		icon: '📧',
		searchValues: ['email', 'mail', 'message', 'communication', 'internet'],
	},
	{
		icon: '📨',
		searchValues: [
			'incoming envelope',
			'mail',
			'letter',
			'message',
			'communication',
		],
	},
	{
		icon: '📩',
		searchValues: ['envelope with arrow', 'mail', 'letter', 'message', 'send'],
	},
	{
		icon: '📤',
		searchValues: ['outbox tray', 'mail', 'send', 'outgoing', 'communication'],
	},
	{
		icon: '📥',
		searchValues: [
			'inbox tray',
			'mail',
			'receive',
			'incoming',
			'communication',
		],
	},

	// Technology
	{
		icon: '💻',
		searchValues: ['laptop', 'computer', 'technology', 'work', 'device'],
	},
	{
		icon: '🖥️',
		searchValues: [
			'desktop computer',
			'computer',
			'technology',
			'work',
			'monitor',
		],
	},
	{
		icon: '⌨️',
		searchValues: ['keyboard', 'computer', 'type', 'input', 'device'],
	},
	{
		icon: '🖱️',
		searchValues: ['computer mouse', 'computer', 'click', 'device', 'pointer'],
	},
	{
		icon: '🖨️',
		searchValues: ['printer', 'print', 'document', 'office', 'device'],
	},
	{
		icon: '📱',
		searchValues: [
			'mobile phone',
			'phone',
			'smartphone',
			'device',
			'communication',
		],
	},
	{
		icon: '📲',
		searchValues: [
			'mobile phone with arrow',
			'phone',
			'call',
			'message',
			'communication',
		],
	},
	{
		icon: '☎️',
		searchValues: ['telephone', 'phone', 'call', 'communication', 'landline'],
	},
	{
		icon: '📞',
		searchValues: [
			'telephone receiver',
			'phone',
			'call',
			'communication',
			'receiver',
		],
	},
	{
		icon: '📟',
		searchValues: ['pager', 'beeper', 'communication', 'device', 'old'],
	},
	{
		icon: '🔋',
		searchValues: ['battery', 'power', 'energy', 'charge', 'device'],
	},
	{
		icon: '🔌',
		searchValues: ['electric plug', 'power', 'electricity', 'charge', 'device'],
	},
	{
		icon: '💽',
		searchValues: ['computer disk', 'storage', 'data', 'old', 'device'],
	},
	{
		icon: '💾',
		searchValues: ['floppy disk', 'save', 'storage', 'data', 'old'],
	},
	{
		icon: '💿',
		searchValues: ['optical disk', 'cd', 'dvd', 'storage', 'data'],
	},
	{ icon: '📀', searchValues: ['dvd', 'disk', 'movie', 'storage', 'data'] },
	{
		icon: '🎥',
		searchValues: ['movie camera', 'film', 'video', 'camera', 'recording'],
	},
	{
		icon: '📹',
		searchValues: ['video camera', 'film', 'video', 'camera', 'recording'],
	},
	{
		icon: '📼',
		searchValues: ['videocassette', 'video', 'tape', 'old', 'recording'],
	},
	{
		icon: '📷',
		searchValues: ['camera', 'photo', 'picture', 'photography', 'device'],
	},
	{
		icon: '📸',
		searchValues: [
			'camera with flash',
			'photo',
			'picture',
			'photography',
			'flash',
		],
	},
	{
		icon: '📽️',
		searchValues: ['film projector', 'movie', 'film', 'projection', 'cinema'],
	},
	{
		icon: '🎞️',
		searchValues: ['film frames', 'movie', 'film', 'frames', 'cinema'],
	},

	// Household Items
	{
		icon: '🔦',
		searchValues: ['flashlight', 'light', 'torch', 'dark', 'tool'],
	},
	{
		icon: '🕯️',
		searchValues: ['candle', 'light', 'flame', 'romantic', 'decor'],
	},
	{
		icon: '💡',
		searchValues: ['light bulb', 'idea', 'light', 'electric', 'bright'],
	},
];
